import { useEffect, useState, useRef, useCallback } from "react";

interface ScrollBehavior {
  /** 0–1: how far user has scrolled */
  scrollProgress: number;
  /** true when user has stopped scrolling for >1.2s */
  isIdle: boolean;
  /** 'slow' | 'normal' | 'fast' */
  scrollSpeed: "slow" | "normal" | "fast";
}

export function useScrollBehavior(): ScrollBehavior {
  const [state, setState] = useState<ScrollBehavior>({
    scrollProgress: 0,
    isIdle: false,
    scrollSpeed: "normal",
  });

  const lastScrollY = useRef(0);
  const lastTime = useRef(Date.now());
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const rafId = useRef<number>();

  // Cache layout-dependent metrics. Reading scrollHeight / innerHeight on
  // every scroll tick forces the browser to flush pending style/layout
  // recalculations (forced reflow). These values only change on resize,
  // orientation change, or when the document size mutates — so we recompute
  // them out-of-band and read from the cache inside the scroll handler.
  const maxScrollRef = useRef(0);

  const recomputeMaxScroll = useCallback(() => {
    maxScrollRef.current =
      document.documentElement.scrollHeight - window.innerHeight;
  }, []);

  const update = useCallback(() => {
    const now = Date.now();
    const dt = now - lastTime.current;
    const scrollY = window.scrollY;
    const dy = Math.abs(scrollY - lastScrollY.current);

    // Pixels per second
    const velocity = dt > 0 ? (dy / dt) * 1000 : 0;
    const speed: ScrollBehavior["scrollSpeed"] =
      velocity < 200 ? "slow" : velocity > 1200 ? "fast" : "normal";

    const maxScroll = maxScrollRef.current;
    const progress = maxScroll > 0 ? Math.min(scrollY / maxScroll, 1) : 0;

    lastScrollY.current = scrollY;
    lastTime.current = now;

    setState((prev) => {
      // Avoid triggering re-renders (and downstream layout reads) when
      // nothing meaningful changed. Quantize progress to ~0.5% steps.
      const quantized = Math.round(progress * 200) / 200;
      const prevQuantized = Math.round(prev.scrollProgress * 200) / 200;
      if (
        prevQuantized === quantized &&
        prev.scrollSpeed === speed &&
        prev.isIdle === false
      ) {
        return prev;
      }
      return {
        ...prev,
        scrollProgress: quantized,
        scrollSpeed: speed,
        isIdle: false,
      };
    });

    // Reset idle timer
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      setState((prev) => (prev.isIdle ? prev : { ...prev, isIdle: true }));
    }, 1200);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(update);
    };

    // Prime the cache after first paint so the initial scroll tick doesn't
    // need to read layout in the hot path.
    const primeId = requestAnimationFrame(recomputeMaxScroll);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recomputeMaxScroll, { passive: true });
    window.addEventListener("orientationchange", recomputeMaxScroll, {
      passive: true,
    });

    // The document height can change without a resize (images decoding,
    // fonts loading, route content expanding). A ResizeObserver on <body>
    // catches those without forcing a layout read on every scroll.
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => recomputeMaxScroll());
      ro.observe(document.body);
    }

    // Initial idle state after page load
    idleTimer.current = setTimeout(() => {
      setState((prev) => ({ ...prev, isIdle: true }));
    }, 2000);

    return () => {
      cancelAnimationFrame(primeId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recomputeMaxScroll);
      window.removeEventListener("orientationchange", recomputeMaxScroll);
      ro?.disconnect();
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [update, recomputeMaxScroll]);

  return state;
}
